import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, ListItemText, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Act } from "../types/act";
import apiClient from "../utils/apiClient";
import { PdfViewer } from "./PdfViewer";
import ActStatus from "../enum/ActStatus.enum";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import ActAction from "../enum/ActAction.enum";

export enum ActPrintType {
  ACT = "act",
  REPORT = "report",
}

interface ActViewerProps {
  act: Act;
  onClose: () => void;
  onAction: (action: ActAction) => void;
  type: ActPrintType;
  lang?: "ru" | "en";
}

export const ActViewer: React.FC<ActViewerProps> = ({
  act,
  onClose = () => {},
  onAction = () => {},
  type = ActPrintType.ACT,
  lang = "ru",
}) => {
  const { t } = useTranslation();

  const isAdmin = useSelector((state: RootState) => state.user.isAdmin);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [actionPending, setActionPending] = useState<boolean>(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | undefined>();
  const [fileName, setFileName] = useState<string>();
  const [error, setError] = useState<string>();

  const isBelarusAct = Number(act?.office) === 84;

  const downloadAct = async () => {
    try {
      setError(undefined);
      setIsLoading(true);

      const response = await apiClient.get(
        `/acts/${act.id}/print/${type}?lang=${lang}`,
        {
          responseType: "arraybuffer",
        },
      );

      if (response?.data) {
        setPdfBlob(new Blob([response.data], { type: "application/pdf" }));
      }

      const contentDisposition = response.headers["content-disposition"];
      let name = `${type}_${act.id}.pdf`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          name = decodeURIComponent(match[1]);
        }
      }

      setFileName(name);
    } catch (error) {
      console.error("Ошибка загрузки файла:", error);
      setError(t("actViewer.downloadError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (act?.id && type) {
      downloadAct();
    }
  }, [act, type, lang]);

  const makeAction = async (action: ActAction) => {
    try {
      setActionPending(true);
      const response = await apiClient.get(`/acts/${act.id}/${action}`);

      if (response.data.success) {
        onAction(action);
      }
    } catch (error) {
      console.error("Ошибка выполнения действия:", error);
    } finally {
      setActionPending(false);
    }
  };

  const Actions = useMemo(() => {
    if (!isAdmin || type === ActPrintType.REPORT || !isBelarusAct) {
      return null;
    }

    if (act?.status === ActStatus.DRAFT) {
      return (
        <Button
          variant="contained"
          color="success"
          onClick={() => makeAction(ActAction.APPROVE)}
          loading={actionPending}
        >
          {t("actViewer.approve")}
        </Button>
      );
    }

    if (act?.status === ActStatus.APPROVED) {
      const approvedAt = act?.approved_at
        ? dayjs(act.approved_at).format("DD.MM.YYYY HH:mm:ss")
        : "";

      return (
        <>
          <Stack spacing={1} direction="row" alignItems="center" sx={{ pr: 2 }}>
            <TaskAltIcon color="success" />
            <ListItemText
              primary={t("actViewer.approved")}
              secondary={`${approvedAt} [${act?.approved_by}]`}
            />
          </Stack>

          <Box>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => makeAction(ActAction.REVOKE)}
              loading={actionPending}
            >
              {t("actViewer.revokeApproval")}
            </Button>
          </Box>
        </>
      );
    }

    return null;
  }, [type, act, actionPending, isAdmin, isBelarusAct, t]);

  return (
    <PdfViewer
      open={act?.id > 0}
      onClose={() => {
        onClose();
        setPdfBlob(undefined);
        setFileName(undefined);
        setError(undefined);
      }}
      title={
        isLoading
          ? t("actViewer.loading")
          : (fileName ?? `${type} ${act?.id ?? ""}`)
      }
      pdfBlob={pdfBlob}
      loading={isLoading}
      footer={Actions}
      error={error}
    />
  );
};
